/**
 * PWA «Доступно обновление» — ядерный вариант применения обновления по клику.
 *
 * ── ЖЕЛЕЗНОЕ ПРАВИЛО (запрос пользователя 2026-06-01, усиливает HARD GATE 5) ──
 * Страница НИКОГДА не перезагружается сама. Reload происходит ИСКЛЮЧИТЕЛЬНО как
 * следствие явного клика пользователя по кнопке «Обновить» в ЭТОЙ вкладке.
 *
 * ── ЯДЕРНЫЙ ВАРИАНТ (2026-06-07, root cause analysis) ──
 * Предыдущий алгоритм (skipWaiting → ждать onNeedReload) ломается в типичном
 * сценарии: force_recache_v* при загрузке страницы unregister'ит все SW. К моменту
 * клика «Обновить» reg.waiting = null — skipWaiting некому слать, onNeedReload
 * не приходит никогда. 10-сек fallback перезагружает, но NetworkFirst runtime-кэш
 * (TTL 24h) отдаёт старый index.html — клик визуально ничего не меняет.
 *
 * Решение: один клик «Обновить» = unregister всех SW + clear всех кэшей + reload.
 * Не зависит от reg.waiting, skipWaiting, onNeedReload — всегда работает,
 * всегда отдаёт свежий контент из сети.
 *
 * HARD GATE 5 соблюдён: reload только из applyPwaUpdate, которая вызывается
 * ИСКЛЮЧИТЕЛЬНО из обработчика клика пользователя.
 */

// Один reload на жизнь страницы (гонка нескольких источников).
let reloadScheduled = false
// true ТОЛЬКО после клика пользователя по «Обновить» в этой вкладке.
let updateInitiatedHere = false

// Legacy key — сохраняем для очистки старого счётчика при необходимости.
const APPLY_KEY = 'onboardix_pwa_apply'

// ── «Обновление только что применено» (FIX 2026-06-08, баг «кнопка не реагирует») ──
// Корень: при открытой второй вкладке (она держит старый SW) после клика «Обновить»
// страница ЯДЕРНО перезагружается на свежий бандл, НО свежезагруженная страница тут же
// снова регистрирует SW; из-за per-deploy SHA-churn (self-heal в index.html) + второй
// вкладки новый SW мгновенно уходит в `waiting` → vite зовёт onNeedRefresh/onNeedReload
// → тост «Доступно обновление» появляется СНОВА. Пользователь уже на самой свежей версии,
// но видит тот же тост → клик «не реагирует» (перезагрузка есть, видимого результата нет).
//
// Лечение: перед reload по клику ставим в sessionStorage метку с таймстампом. Свежая
// страница в течение grace-окна подавляет повторный тост (обновлять уже нечего — это и
// есть самая новая версия). sessionStorage переживает ядерный reload (тот же таб/origin,
// чистятся только SW + CacheStorage) и сбрасывается при закрытии вкладки — корректно:
// новая сессия = честный новый старт. HARD GATE 5 соблюдён: reload по-прежнему ТОЛЬКО
// по клику, никакого auto-reload здесь не добавляется.
const JUST_APPLIED_KEY = 'onboardix_pwa_just_applied'
const JUST_APPLIED_GRACE_MS = 60_000

/** sessionStorage может бросать в приватных режимах — гасим к null. */
function safeSessionStorage() {
    try {
        return typeof sessionStorage !== 'undefined' ? sessionStorage : null
    } catch {
        return null
    }
}

/** Реальный reload с правильным this. */
function defaultReload() {
    window.location.reload()
}

/** localStorage может бросать в приватных режимах — гасим к null. */
function safeLocalStorage() {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null
    } catch {
        return null
    }
}

function reloadOnce(reload) {
    if (reloadScheduled) return
    reloadScheduled = true
    reload()
}

/** Сбросить счётчик кликов (legacy — сохраняем экспорт для compat). */
export function clearApplyCount({ storage = safeLocalStorage() } = {}) {
    try { storage?.removeItem(APPLY_KEY) } catch { /* noop */ }
}

/**
 * Применить PWA-обновление в ответ на ЯВНЫЙ клик пользователя «Обновить».
 *
 * Ядерный вариант: unregister всех SW → clear всех кэшей → window.location.reload().
 * Гарантированно доставляет свежий контент, не зависит от reg.waiting или onNeedReload.
 *
 * Параметры skipWaiting / registration / getRegistration / onStuck / fallbackMs / now
 * принимаются для обратной совместимости с вызывающим кодом, но не используются.
 */
export async function applyPwaUpdate({
    reload = defaultReload,
    // Legacy params — принимаются, игнорируются
    skipWaiting,
    registration,
    getRegistration,
    onStuck,
    fallbackMs,
    storage = safeLocalStorage(),
    session = safeSessionStorage(),
    now = () => Date.now(),
} = {}) {
    // Клик — санкция на перезагрузку этой вкладки.
    updateInitiatedHere = true
    if (reloadScheduled) return

    // Метим, что апдейт применён ПО КЛИКУ — свежезагруженная страница подавит
    // повторный тост в течение grace-окна (см. wasUpdateJustApplied / шапку файла).
    try { session?.setItem(JUST_APPLIED_KEY, String(now())) } catch { /* noop */ }

    // Unregister SW + clear caches (best effort).
    // В тестовом окружении (jsdom) navigator.serviceWorker и caches недоступны —
    // блоки молча пропускаются, reload() вызывается напрямую.
    try {
        if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
            const regs = await navigator.serviceWorker.getRegistrations()
            await Promise.all(regs.map(r => r.unregister()))
        }
        if (typeof caches !== 'undefined') {
            const keys = await caches.keys()
            await Promise.all(keys.map(k => caches.delete(k)))
        }
    } catch { /* best effort */ }

    reloadOnce(reload)
}

/**
 * Был ли апдейт применён по клику «Обновить» в этой же сессии вкладки за последние
 * JUST_APPLIED_GRACE_MS. Если да — тост «Доступно обновление» показывать НЕ нужно:
 * страница уже на самой свежей версии, повторный тост = иллюзия «кнопка не работает».
 *
 * Метка одноразовая в пределах grace-окна: первый положительный вызов её гасит, чтобы
 * последующие легитимные апдейты (новый деплой через минуты работы) снова показывали тост.
 *
 * @returns {boolean} true если только что применили апдейт → тост подавить.
 */
export function wasUpdateJustApplied({ session = safeSessionStorage(), now = () => Date.now() } = {}) {
    if (!session) return false
    let raw = null
    try { raw = session.getItem(JUST_APPLIED_KEY) } catch { return false }
    if (!raw) return false
    const ts = Number(raw)
    const fresh = Number.isFinite(ts) && (now() - ts) >= 0 && (now() - ts) <= JUST_APPLIED_GRACE_MS
    // Гасим метку в любом случае: устаревшую — чтобы не копилась; свежую — чтобы
    // подавление сработало РОВНО один раз на пост-reload показ, а не навсегда.
    try { session.removeItem(JUST_APPLIED_KEY) } catch { /* noop */ }
    return fresh
}

/**
 * Новый SW взял управление (vite `onNeedReload`). Перезагружаем вкладку ТОЛЬКО
 * если апдейт инициирован здесь кликом. Иначе — НЕ трогаем страницу (HARD GATE 5).
 *
 * @returns {boolean} true если вкладка перезагружается, false если нужно показать тост.
 */
export function handleSwTookControl({ reload = defaultReload, storage = safeLocalStorage() } = {}) {
    if (!updateInitiatedHere) return false
    clearApplyCount({ storage })
    reloadOnce(reload)
    return true
}

/** test-only: сбросить guard + флаг инициатора между кейсами. */
export function __resetReloadGuard() {
    reloadScheduled = false
    updateInitiatedHere = false
}
